import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import {
  Scissors,
  Menu,
  X,
  Home,
  Info,
  Calendar,
  Briefcase,
  LayoutDashboard,
  Settings,
  User,
  LogOut,
  LogIn,
  Phone,
} from 'lucide-react';
import './Navigation.css';

function Navigation() {
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const activePath = location.pathname;
  const userLabel = useMemo(() => user?.firstName || 'Profile', [user]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const linkClass = (path, extra = '') => `nav-link ${activePath === path ? 'active' : ''} ${extra}`.trim();

  const navLinks = (
    <>
      <Link to="/" className={linkClass('/')} onClick={closeMobileMenu}>
        <Home size={15} />
        <span>Home</span>
      </Link>
      <Link to="/about" className={linkClass('/about')} onClick={closeMobileMenu}>
        <Info size={15} />
        <span>About</span>
      </Link>
      <Link to="/contact" className={linkClass('/contact')} onClick={closeMobileMenu}>
        <Phone size={15} />
        <span>Contact</span>
      </Link>
      <Link to="/careers" className={linkClass('/careers')} onClick={closeMobileMenu}>
        <Briefcase size={15} />
        <span>Careers</span>
      </Link>

      <Link to="/booking" className={linkClass('/booking', 'nav-link-booking')} onClick={closeMobileMenu}>
        <Calendar size={15} />
        <span>Book</span>
      </Link>

      {isAuthenticated ? (
        <>
          {user?.role === 'admin' && (
            <>
              <Link to="/admin" className={linkClass('/admin')} onClick={closeMobileMenu}>
                <LayoutDashboard size={15} />
                <span>Admin</span>
              </Link>
              <Link to="/config" className={linkClass('/config')} onClick={closeMobileMenu}>
                <Settings size={15} />
                <span>Config</span>
              </Link>
            </>
          )}
          {user?.role === 'barber' && (
            <Link
              to="/barber-dashboard"
              className={linkClass('/barber-dashboard')}
              onClick={closeMobileMenu}
            >
              <LayoutDashboard size={15} />
              <span>My Schedule</span>
            </Link>
          )}
          <Link to="/profile" className={linkClass('/profile', 'nav-link-user')} onClick={closeMobileMenu}>
            <User size={15} />
            <span>{userLabel}</span>
          </Link>
          <button
            type="button"
            onClick={() => {
              logout();
              closeMobileMenu();
            }}
            className="nav-link nav-link-logout"
          >
            <LogOut size={15} />
            <span>Logout</span>
          </button>
        </>
      ) : (
        <Link to="/login" className={linkClass('/login', 'nav-link-login')} onClick={closeMobileMenu}>
          <LogIn size={15} />
          <span>Login</span>
        </Link>
      )}
    </>
  );

  return (
    <header className={`site-nav ${scrolled ? 'site-nav--scrolled' : ''}`}>
      <div className="site-nav__inner main-shell">
        <Link to="/" className="site-nav__brand" onClick={closeMobileMenu}>
          <span className="site-nav__brand-icon" aria-hidden="true">
            <Scissors size={18} />
          </span>
          <div className="site-nav__brand-copy">
            <strong>Balkan Barber</strong>
            <span>Downtown Jersey City</span>
          </div>
        </Link>

        <button
          type="button"
          className="site-nav__toggle"
          aria-label="Toggle navigation menu"
          onClick={() => setMobileMenuOpen((v) => !v)}
        >
          {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        {/* Desktop nav */}
        <nav className="site-nav__links site-nav__links--desktop">
          {navLinks}
        </nav>

        {/* Mobile nav with animation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.nav
              className="site-nav__links site-nav__links--mobile"
              initial={{ opacity: 0, y: -10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            >
              {navLinks}
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}

export default Navigation;
